import {
  ComponentInternalInstance,
  computed,
  ComputedRef,
  getCurrentInstance,
  onBeforeUnmount,
  reactive,
} from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import { SubTask, Task, TaskStatus, TaskManagerList } from '@/renderer/scripts/utils/TaskManager/types';
import { FileInfo, TeamPathInfo } from '@/renderer/scripts/utils/upload/types';
import { DownloadFileInfo } from '@/renderer/scripts/utils/download/types';
import uploadTaskManager from '@/renderer/scripts/utils/upload/index';
import downloadTaskManager from '@/renderer/scripts/utils/download/index';
import storage from '@/renderer/scripts/utils/storage';
import smh from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import { IS_PC } from '@/common/constant/platform';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';

let fs: typeof import('fs');
let path: typeof import('path');
let electron: typeof import('electron');
if (IS_PC) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
  electron = (window.parent as any).require('electron') as typeof import('electron');
}

interface Handler {
  uploadList: ComputedRef<Task<FileInfo>[]>;
  upListTotal: ComputedRef<Task<FileInfo>[]>;
  downloadList: ComputedRef<Task<DownloadFileInfo>[]>;
  finishList: ComputedRef<Task<FileInfo | DownloadFileInfo>[]>;
  hasRunningTask: ComputedRef<boolean>;
  handleTransmissionOperate: (command: string, task: Task<FileInfo>, transmissionType: string) => void;
  handleFinishOperate: (command: string, task: Task<FileInfo>) => void;
}
export default function transmissionListHandle(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const { $router } = currComponent.proxy;
  const uploadManagerList = reactive<TaskManagerList<FileInfo>>({
    running: [],
    waiting: [],
    finish: [],
    list: [],
  });
  const downloadManagerList = reactive<TaskManagerList<DownloadFileInfo>>({
    running: [],
    waiting: [],
    finish: [],
    list: [],
  });
  const uploadList = computed<any>(
    () => [...uploadManagerList.running, ...uploadManagerList.waiting] as Task<FileInfo>[],
  );
  const upListTotal = computed<any>(() => [
    ...uploadManagerList.running,
    ...uploadManagerList.waiting,
    ...uploadManagerList.finish,
  ]);
  const finishList = computed<Task<FileInfo | DownloadFileInfo>[]>(() => {
    const uploadFinished: Task<FileInfo>[] = uploadManagerList.finish as Task<FileInfo>[];
    const downloadFinished: Task<DownloadFileInfo>[] = downloadManagerList.finish as Task<DownloadFileInfo>[];
    return [...uploadFinished, ...downloadFinished].sort((a, b) => (b.finishDate as number) - (a.finishDate as number));
  });
  const downloadList = computed<Task<DownloadFileInfo>[]>(() => [
    ...downloadManagerList.running,
    ...downloadManagerList.waiting,
  ]);

  // 传输列表中有正在进行的任务
  const hasRunningTask = computed(() => {
    const uploadRunningStatus = [TaskStatus.ready, TaskStatus.preparing, TaskStatus.checking, TaskStatus.running];
    const hasRunningUpload = uploadList.value.some((item: any) => {
      return uploadRunningStatus.includes(item.status);
    });
    if (hasRunningUpload) return true;
    const hadRunningDownload = downloadList.value.some((item: any) => {
      return item.status === 'running' || item.status === 'checking';
    });
    return hadRunningDownload;
  });
  const methods = {
    // eslint-disable-next-line max-len
    openRemotePath(params: {
      remoteFolder: string;
      routerName: string;
      teamPathInfo?: TeamPathInfo;
      spaceId?: string;
    }) {
      const { remoteFolder, routerName, teamPathInfo, spaceId } = params;
      const index = remoteFolder?.split('/').length;
      if (routerName === 'teamSpace' && teamPathInfo) {
        // 团队空间下，teamPathInfo下的path保存的团队的路径，文件的路径为remoteFolder
        storage.set(storage.StoreKey.TEAM_PATH, teamPathInfo);
      } else if (routerName === 'relatedToMeFileList') {
        storage.set(storage.StoreKey.RECV_SHARE_SPACE, {
          spaceId,
          crumbStartIndex: index,
        });
      }
      const remoteParams = {
        routerName,
        folderPath: remoteFolder?.split('/'), // 此处path需数组类型，会被teamPathInfo覆盖，改名folderPath
      };

      if (teamPathInfo) {
        Object.assign(remoteParams, teamPathInfo); // 此处teamPathInfo里path覆盖前面的
      }

      storage.set(storage.StoreKey.REMOTE_PATH, remoteParams);
      // 从web端传输列表打开上传的目标目录，通过新的页面进行路由中转, 以适配同空间下不同路径以及不同共享空间的切换
      dispatchRouter(
        {
          name: 'transit',
        },
        'replace',
      );
    },
    updateUploadList(params: TaskManagerList<FileInfo>) {
      uploadManagerList.running = params.running;
      uploadManagerList.waiting = params.waiting;
      uploadManagerList.finish = params.finish;
    },
    updateDownloadList(params: TaskManagerList<DownloadFileInfo>) {
      downloadManagerList.running = params.running;
      downloadManagerList.waiting = params.waiting;
      downloadManagerList.finish = params.finish;
    },
    /**
     * @description 处理传输列表操作
     * @param {string} command
     * @param {Task<FileInfo>} task
     */
    async handleTransmissionOperate(
      command: string,
      task: Task<FileInfo | DownloadFileInfo>,
      transmissionType: string,
    ) {
      switch (command) {
        case 'pauseTask':
        case 'restartTask':
        case 'retryTask':
        case 'cancelTask':
          if (transmissionType === 'upload') {
            uploadTaskManager[command](task.taskId);
          } else {
            downloadTaskManager[command](task.taskId);
          }
          break;
        case 'pause':
        case 'restart':
        case 'cancel':
          if (transmissionType === 'upload') {
            uploadTaskManager[command]();
          } else {
            downloadTaskManager[command]();
          }
          break;
        case 'openFolder':
          if (task.action === 'upload') {
            if (task.error) {
              const list = task.getList() as SubTask<FileInfo>[];
              const sortList = list.sort((a, b) => a.info.localPath.length - b.info.localPath.length);
              const { localPath, name } = sortList[0].info;
              const localRootFolderPath = localPath.slice(0, -name.length);
              if (!fs.existsSync(localRootFolderPath)) {
                tips.error(task.isDir ? i18n_t('本地目录不存在') : i18n_t('本地文件不存在'));
                return;
              }
              electron.shell.showItemInFolder(localRootFolderPath);
            } else {
              methods.openRemotePath(task.getItem(0).info as FileInfo);
            }
          } else {
            // Web端下载用的浏览器自带下载器，因此无需处理错误
            const list = task.getList() as SubTask<DownloadFileInfo>[];
            const sortList = list.sort((a, b) => a.info.filePath.length - b.info.filePath.length);
            const [
              {
                info: { filePath, name, routerName, teamPathInfo, spaceId, spaceOrgId },
              },
            ] = sortList;
            const remoteFolder = filePath.slice(0, -name.length).replace(/\/$/, '');
            try {
              // 检查远端目录是否存在
              await smh.userAPI.getDirectoryOrFileInfo({
                path: remoteFolder,
                spaceId,
                spaceOrgId: spaceOrgId || store.getters.orgId,
              });
              methods.openRemotePath({
                remoteFolder,
                routerName: routerName as string,
                teamPathInfo,
                spaceId,
              });
            } catch (error) {
              tips.error(error as MsgError);
            }
          }
          break;
        default:
          break;
      }
    },
    /**
     * @description 处理已完成列表的操作
     * @param {string} command
     * @param {Task<FileInfo>} task
     */
    handleFinishOperate(command: string, task: Task<FileInfo | DownloadFileInfo>) {
      switch (command) {
        case 'cancelTask':
        case 'restartTask':
          if (task.action === 'upload') {
            uploadTaskManager[command](task.taskId);
          } else {
            downloadTaskManager[command](task.taskId);
          }
          break;
        case 'cancel':
          uploadTaskManager[command]('finish');
          downloadTaskManager[command]('finish');
          break;
        case 'openFolder':
          // pc端打开已上传文件目录
          if (task.action === 'upload') {
            methods.openRemotePath(task.getItem(0).info as FileInfo);
          } else {
            const { downloadTarget, savePath, saveRootFolderName } = task.getItem(0).info as DownloadFileInfo;
            const openPath = task.isDir ? path.resolve(downloadTarget, saveRootFolderName) : (savePath as string);
            if (!fs.existsSync(openPath)) {
              tips.error(task.isDir ? i18n_t('本地目录不存在') : i18n_t('本地文件不存在'));
              return;
            }
            electron.shell.showItemInFolder(openPath);
          }
          break;
        default:
          break;
      }
    },
  };
  methods.updateUploadList(uploadTaskManager.getList());
  uploadTaskManager.on('list-update', methods.updateUploadList);
  onBeforeUnmount(() => {
    uploadTaskManager.off('list-update', methods.updateUploadList);
  });
  methods.updateDownloadList(downloadTaskManager.getList());
  downloadTaskManager.on('list-update', methods.updateDownloadList);
  onBeforeUnmount(() => {
    downloadTaskManager.off('list-update', methods.updateDownloadList);
  });
  return {
    uploadList,
    upListTotal,
    finishList,
    downloadList,
    hasRunningTask,
    handleTransmissionOperate: methods.handleTransmissionOperate,
    handleFinishOperate: methods.handleFinishOperate,
  };
}
